import { css, html, LitElement, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";

/**
 * This component registers controls for a sticky banner feature in
 * the webshop.
 *
 * This banner is registered as a custom web component and has logic
 * to hide when nearing a different light-dom element.
 *
 * TODO:
 * - Add tests
 * - Add documentation
 * - Add custom properties for better styling on a per component level.
 */
class StickyBanner extends LitElement {
	/**
	 * A media query string that defines when the banner is visible.
	 * If not set, the banner is always visible.
	 */
	@property({ attribute: "visible-breakpoint" }) visibleBreakpoint?: string;

	/**
	 * A CSS selector for an element that, when visible, will hide the banner.
	 *
	 * Multiple selectors can be supplied using a comma-separated list.
	 */
	@property({ attribute: "hide-near-selector" }) hideNearSelector?: string;

	/**
	 * Whether the banner is hidden.
	 */
	@property({ attribute: "banner-hidden", reflect: true, type: Boolean }) bannerHidden = false;

	@state() hideForSelector = false;
	@state() hideForBreakpoint = false;

	private intersectionObserver?: IntersectionObserver;

	override connectedCallback() {
		super.connectedCallback();
		this.setupIntersectionObserver();
		this.setupMediaQueryListener();
	}
	override disconnectedCallback() {
		super.disconnectedCallback();
		this.intersectionObserver?.disconnect();
	}

	protected override updated(_changedProperties: PropertyValues): void {
		super.updated(_changedProperties);

		if (_changedProperties.has("hideForSelector")) {
			this.bannerHidden = [this.hideForSelector, this.hideForBreakpoint].some((value) => value === true);
		}

		if (_changedProperties.has("hideForBreakpoint")) {
			this.bannerHidden = [this.hideForSelector, this.hideForBreakpoint].some((value) => value === true);
		}
	}

	private setupIntersectionObserver() {
		if (!this.hideNearSelector) return;
		const selectors = this.hideNearSelector.split(",").map((s) => s.trim());
		const targetElements = selectors.flatMap((selector) => document.querySelectorAll<HTMLElement>(selector));
		if (targetElements.length === 0) return;

		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					this.hideForSelector = entry.isIntersecting;
				});
			},
			{ threshold: [0, 1] },
		);

		targetElements.forEach((elements: NodeListOf<HTMLElement>) => {
			elements.forEach((e) => {
				this.intersectionObserver?.observe(e);
			});
		});
	}

	private setupMediaQueryListener() {
		if (!this.visibleBreakpoint) return;
		const mediaQueryList = globalThis.matchMedia(this.visibleBreakpoint);
		const updateVisibility = (e: MediaQueryListEvent | MediaQueryList) => {
			this.hideForBreakpoint = !e.matches;
		};
		updateVisibility(mediaQueryList);
		mediaQueryList.addEventListener("change", updateVisibility);
	}

	override render() {
		return html`<slot></slot>`;
	}

	static override styles = [
		css`
      :host {
        position: fixed;
        bottom: 0;
        inset-inline: 0;
        padding: 1rem;
        background: var(--background);
        background-image: var(--background-gradient);
        z-index: 100;
        transition: transform 0.3s ease-out;
      }

      :host-context([banner-hidden]),
      :host[banner-hidden] {
        transform: translateY(100%);
        transition-timing-function: ease-in;
      }
    `,
	];
}

declare global {
	interface HTMLElementTagNameMap {
		"y-sticky-banner": StickyBanner;
	}
}

if (!customElements.get("y-sticky-banner")) {
	customElements.define("y-sticky-banner", StickyBanner);
}
